import { type CallWindow } from '@kalos/kalos-rpc';

/** A window's effective limits when a department overrides the window's own values. */
export type WindowLimitOverride = { windowCount: number; maintMax: number };

/** Key into a date-override map: one window on one date. */
export const windowDateKey = (callWindowId: number, date: string) =>
  `${callWindowId}:${date.slice(0, 10)}`;

/**
 * A window's effective limits on a given date, highest precedence first:
 *
 *   1. a date override for that window on that date (call_window_limit_date)
 *   2. this department's weekly override of the window (call_window_limit)
 *   3. the window's own values (call_window)
 *
 * windowCount and maintMax are resolved together from whichever source wins, rather than
 * picked independently: they always come from one override row, and the limits dialog seeds
 * every field of a date row from the weekly value before saving, so a date row that only
 * changes max jobs still carries the weekly maintMax.
 *
 * windowCount 0 means "no max configured" (the column default), not "no jobs allowed" — the
 * limits dialog floors its stepper at 1 so 0 isn't reachable from the UI. maintMax -1 means no
 * limit, 0 means none.
 *
 * `date` and `dateOverrides` are required rather than optional so a caller cannot silently
 * fall back to weekly-only behaviour by forgetting them — the bug this layer exists to fix.
 * Pass an empty map where date overrides genuinely don't apply.
 */
export function resolveWindowLimits(
  window: Pick<CallWindow, 'id' | 'windowCount' | 'maintMax'>,
  overrides: Record<number, WindowLimitOverride>,
  dateOverrides: Record<string, WindowLimitOverride>,
  date: string,
): WindowLimitOverride {
  const dateOverride = date ? dateOverrides[windowDateKey(window.id, date)] : undefined;
  const override = dateOverride ?? overrides[window.id];
  return {
    windowCount: override?.windowCount ?? window.windowCount,
    maintMax: override?.maintMax ?? window.maintMax,
  };
}

/**
 * Whether a window's limits on a date come from a date override rather than from the weekly
 * value — what the scheduler marks on the cap and what the dialog marks on a row.
 */
export function hasDateOverride(
  windowId: number,
  dateOverrides: Record<string, WindowLimitOverride>,
  date: string,
): boolean {
  return !!date && !!dateOverrides[windowDateKey(windowId, date)];
}
